import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Flag,
  Tag,
  Clock,
  Calendar,
  User,
  Phone,
  MoreVertical,
  Edit3,
  Save,
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Forward,
  Maximize2,
  RefreshCw,
  Loader2,
  Lock,
  ShieldCheck,
} from "lucide-react";
import {
  ExportCurve,
  DocumentDownload,
  Profile,
  VolumeHigh,
  VolumeCross,
} from "iconsax-reactjs";
import AskGenziButton from "./AskGenziButton";
import api from "../../services/api";

const CallDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [call, setCall] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [isRequalifying, setIsRequalifying] = useState(false);

  // Password protection states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  // Audio playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);
  const transcriptContainerRef = useRef(null);
  const transcriptRefs = useRef({});
  const verifiedPasswordRef = useRef(null); // Holds the verified password for re-fetches
  const [reprocessing, setReprocessing] = useState({
    transcribing: false,
    analyzing: false,
    processing: false,
  });
  const [reprocessError, setReprocessError] = useState(null);
  const [reprocessSuccess, setReprocessSuccess] = useState(null);

  useEffect(() => {
    checkPasswordStatus();
  }, [id]);

  const checkPasswordStatus = async () => {
    try {
      const response = await api.checkRecordingPasswordStatus(id);
      setIsPasswordProtected(response.isPasswordProtected);

      if (!response.isPasswordProtected) {
        setPasswordVerified(true);
        fetchCallDetails();
      } else {
        // Recording is protected — stop the spinner so the password gate renders
        setLoading(false);
      }
    } catch (error) {
      console.error("Error checking password status:", error);
      // If check fails, proceed to load call details anyway
      setPasswordVerified(true);
      fetchCallDetails();
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      const response = await api.verifyRecordingPassword(id, password);

      if (response.valid) {
        verifiedPasswordRef.current = password; // Store for all subsequent fetches
        setPasswordVerified(true);
        setShowPasswordModal(false);
        fetchCallDetails(password); // Forward password to getCallDetails
      } else {
        alert("Incorrect password. Please try again.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        alert("Incorrect password. Please try again.");
      } else {
        alert("An error occurred verifying the password. Please try again.");
      }
    }
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) {
      alert("Password must be at least 6 characters.");
      return;
    }

    try {
      await api.setRecordingPassword(id, newPassword);
      alert("Password set successfully.");
      setShowSetPasswordModal(false);
      setNewPassword("");
      setIsPasswordProtected(true);
    } catch (error) {
      alert("Failed to set password.");
    }
  };

  const handleRemovePassword = async () => {
    if (!window.confirm("Remove password protection from this recording?")) {
      return;
    }

    try {
      await api.removeRecordingPassword(id);
      alert("Password removed successfully.");
      verifiedPasswordRef.current = null; // Clear stored password
      setIsPasswordProtected(false);
    } catch (error) {
      alert("Failed to remove password.");
    }
  };

  const fetchCallDetails = async (pwd = null) => {
    // Use explicitly passed password, fall back to the stored verified password
    const passwordToUse = pwd ?? verifiedPasswordRef.current;
    try {
      setLoading(true);
      const response = await api.getCallDetails(id, passwordToUse);

      if (response.success) {
        setCall(response.call);
        setNotes(response.call.metadata?.notes || "");
        setTags(response.call.insights?.tags || []);
        setAudioDuration(response.call.duration || 0);
      } else {
        setError("Failed to fetch call details");
      }
    } catch (error) {
      // If the backend rejects with requiresPassword, show the password gate again
      if (
        error.response?.status === 401 &&
        error.response?.data?.requiresPassword
      ) {
        verifiedPasswordRef.current = null;
        setIsPasswordProtected(true);
        setPasswordVerified(false);
        setLoading(false);
        return;
      }
      console.error("Error fetching call details:", error);
      setError(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Handle re-qualification
  const handleRequalify = async () => {
    if (
      !window.confirm(
        "Are you sure you want to re-qualify this call? This will update all qualification data.",
      )
    ) {
      return;
    }

    try {
      setIsRequalifying(true);
      const response = await api.requalifyCall(id);

      if (response.success) {
        // Refresh call details to get updated qualification
        await fetchCallDetails();

        // Show success message with score
        const score = response.qualification?.qualificationScore || 0;
        const level = response.qualification?.qualificationLevel || "unknown";
        alert(
          `✓ Call re-qualified successfully!\n\nNew Score: ${score}/100\nLevel: ${level.toUpperCase()}`,
        );
      } else {
        throw new Error(response.message || "Re-qualification failed");
      }
    } catch (error) {
      console.error("Error re-qualifying call:", error);
      alert(
        "Failed to re-qualify call:\n" +
          (error.response?.data?.message || error.message),
      );
    } finally {
      setIsRequalifying(false);
    }
  };
  // Re-processing handlers
  const handleReTranscribe = async () => {
    if (
      !window.confirm(
        "Re-transcribe this call? This will delete existing transcripts and regenerate them. Audio will be processed again.",
      )
    ) {
      return;
    }

    try {
      setReprocessing({ ...reprocessing, transcribing: true });
      setReprocessError(null);
      setReprocessSuccess(null);

      const response = await api.reTranscribeCall(id);

      if (response.success) {
        setReprocessSuccess(
          `✓ Call re-transcribed successfully! ${response.transcriptCount} segments generated.`,
        );
        // Refresh call details
        await fetchCallDetails();
      }
    } catch (error) {
      console.error("Re-transcribe error:", error);
      setReprocessError(
        `Failed to re-transcribe: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setReprocessing({ ...reprocessing, transcribing: false });
    }
  };

  const handleReAnalyze = async () => {
    if (
      !window.confirm(
        "Re-analyze this call? This will regenerate sentiment analysis and insights.",
      )
    ) {
      return;
    }

    if (!call.transcripts || call.transcripts.length === 0) {
      alert("This call must have transcripts before it can be re-analyzed.");
      return;
    }

    try {
      setReprocessing({ ...reprocessing, analyzing: true });
      setReprocessError(null);
      setReprocessSuccess(null);

      const response = await api.reAnalyzeCall(id);

      if (response.success) {
        setReprocessSuccess(
          "✓ Call re-analyzed successfully! Insights regenerated.",
        );
        // Refresh call details
        await fetchCallDetails();
      }
    } catch (error) {
      console.error("Re-analyze error:", error);
      setReprocessError(
        `Failed to re-analyze: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setReprocessing({ ...reprocessing, analyzing: false });
    }
  };

  const handleReProcess = async () => {
    if (
      !window.confirm(
        "Completely re-process this call? This will:\n\n• Re-transcribe the audio\n• Re-analyze sentiment and insights\n• Re-qualify the lead\n\nThis may take several minutes.",
      )
    ) {
      return;
    }

    try {
      setReprocessing({ ...reprocessing, processing: true });
      setReprocessError(null);
      setReprocessSuccess(null);

      const response = await api.reProcessCall(id);

      if (response.success) {
        setReprocessSuccess(
          `✓ Call re-processed successfully!\n• ${response.transcriptCount} transcript segments\n• ${
            response.hasInsights ? "Insights regenerated" : "No insights"
          }\n• ${response.hasQualification ? "Lead re-qualified" : "Not qualified"}`,
        );
        // Refresh call details
        await fetchCallDetails();
      }
    } catch (error) {
      console.error("Re-process error:", error);
      setReprocessError(
        `Failed to re-process: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setReprocessing({ ...reprocessing, processing: false });
    }
  };
  // Audio playback functions
  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration || 0);
      setCurrentTime(0);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(Math.floor(audioRef.current.currentTime));
      scrollToCurrentTranscript(audioRef.current.currentTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleSeek = (e) => {
    if (audioRef.current && audioDuration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const percentage = Math.min(1, Math.max(0, clickX / width));
      const newTime = percentage * audioDuration;

      audioRef.current.currentTime = newTime;
      setCurrentTime(Math.floor(newTime));
    }
  };

  // Scroll transcript to current audio position
  const scrollToCurrentTranscript = (currentTimeInSeconds) => {
    if (!call?.transcripts || !transcriptContainerRef.current) return;

    const currentTranscriptIndex = call.transcripts.findIndex(
      (transcript, index) => {
        const startTime = parseFloat(transcript.startTime);
        const nextTranscript = call.transcripts[index + 1];
        const endTime = nextTranscript
          ? parseFloat(nextTranscript.startTime)
          : audioDuration;

        return (
          currentTimeInSeconds >= startTime && currentTimeInSeconds < endTime
        );
      },
    );

    if (currentTranscriptIndex !== -1) {
      const transcriptElement = transcriptRefs.current[currentTranscriptIndex];
      if (transcriptElement && transcriptContainerRef.current) {
        const container = transcriptContainerRef.current;
        const elementTop = transcriptElement.offsetTop;
        const containerHeight = container.clientHeight;
        const elementHeight = transcriptElement.clientHeight;

        const scrollPosition =
          elementTop - containerHeight / 2 + elementHeight / 2;
        container.scrollTo({
          top: scrollPosition,
          behavior: "smooth",
        });
      }
    }
  };

  // Click on transcript timestamp to seek audio
  const handleTranscriptTimeClick = (startTime) => {
    if (audioRef.current) {
      const timeInSeconds = parseFloat(startTime);
      audioRef.current.currentTime = timeInSeconds;
      setCurrentTime(Math.floor(timeInSeconds));

      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleSaveNotes = async () => {
    try {
      await api.updateCallMetadata(id, { notes });
      setEditingNotes(false);
      fetchCallDetails();
    } catch (error) {
      console.error("Error saving notes:", error);
      alert("Failed to save notes");
    }
  };

  const handleExportTranscript = async () => {
    try {
      const transcriptContent = call.transcripts
        .map((transcript) => {
          const time = formatTranscriptTime(transcript.startTime);
          const speaker = transcript.speakerLabel || "Speaker";
          return `[${time}] ${speaker}: ${transcript.text}`;
        })
        .join("\n\n");

      const blob = new Blob([transcriptContent], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `transcript-${call.id}-${Date.now()}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting transcript:", error);
      alert("Failed to export transcript");
    }
  };

  const handleDownloadAudio = () => {
    if (!call.recordingUrl) {
      alert("No audio recording available for download");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = call.recordingUrl;
      const filename =
        call.recordingUrl.split("/").pop() || `call-recording-${call.id}.mp3`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading audio:", error);
      alert("Failed to download audio file");
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      const updatedTags = [...tags, newTag.trim()];
      setTags(updatedTags);
      setNewTag("");

      api
        .updateCallMetadata(id, { tags: updatedTags })
        .then(() => {
          fetchCallDetails();
        })
        .catch((error) => {
          console.error("Error saving tags:", error);
          setTags(tags);
        });
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(updatedTags);

    api
      .updateCallMetadata(id, { tags: updatedTags })
      .then(() => {
        fetchCallDetails();
      })
      .catch((error) => {
        console.error("Error saving tags:", error);
        setTags(tags);
      });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes === 0) return `${secs}s`;
    return `${minutes}m ${secs}s`;
  };

  const formatTime = (seconds) => {
    const secs = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins}:${rem.toString().padStart(2, "0")}`;
  };

  const formatTranscriptTime = (secondsString) => {
    const seconds = parseFloat(secondsString);
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case "positive":
        return "text-green-400";
      case "negative":
        return "text-red-400";
      case "neutral":
        return "text-yellow-400";
      default:
        return "text-gray-400";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "text-green-400 bg-green-400/10";
      case "processing":
      case "transcribing":
      case "analyzing":
        return "text-orange-400 bg-orange-400/10";
      case "failed":
        return "text-red-400 bg-red-400/10";
      case "uploading":
      case "uploaded":
        return "text-blue-400 bg-blue-400/10";
      default:
        return "text-gray-400 bg-gray-400/10";
    }
  };

  const isTranscriptActive = (startTime, index) => {
    const start = parseFloat(startTime);
    const nextTranscript = call?.transcripts[index + 1];
    const end = nextTranscript
      ? parseFloat(nextTranscript.startTime)
      : audioDuration;

    return currentTime >= start && currentTime < end;
  };

  // Qualification Details Component
  const QualificationDetails = ({ qualification }) => {
    if (!qualification) {
      return (
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Call Qualification</h2>
            <button
              onClick={handleRequalify}
              disabled={isRequalifying || !call?.transcripts?.length}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                !call?.transcripts?.length
                  ? "Call must have transcripts to be qualified"
                  : "Qualify this call"
              }
            >
              {isRequalifying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Qualifying...</span>
                </>
              ) : (
                <>
                  <Flag size={16} />
                  <span>Qualify Call</span>
                </>
              )}
            </button>
          </div>
          <p className="text-gray-400 text-sm">
            {!call?.transcripts?.length
              ? "This call needs transcripts before it can be qualified."
              : "This call has not been qualified yet. Click the button above to qualify it."}
          </p>
        </div>
      );
    }

    const getScoreColor = (score) => {
      if (score >= 75) return "text-green-400";
      if (score >= 50) return "text-yellow-400";
      if (score >= 25) return "text-orange-400";
      return "text-red-400";
    };

    const getScoreBgColor = (score) => {
      if (score >= 75) return "bg-green-400/10";
      if (score >= 50) return "bg-yellow-400/10";
      if (score >= 25) return "bg-orange-400/10";
      return "bg-red-400/10";
    };

    const getLevelColor = (level) => {
      switch (level) {
        case "high":
          return "text-green-400 bg-green-400/10";
        case "medium":
          return "text-yellow-400 bg-yellow-400/10";
        case "low":
          return "text-orange-400 bg-orange-400/10";
        default:
          return "text-red-400 bg-red-400/10";
      }
    };

    const getQualityColor = (quality) => {
      switch (quality) {
        case "excellent":
          return "text-green-400";
        case "good":
          return "text-blue-400";
        case "poor":
          return "text-orange-400";
        case "skipped":
          return "text-red-400";
        default:
          return "text-gray-400";
      }
    };

    return (
      <div className="space-y-6">
        {/* Overall Scores with Re-qualify Button */}
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold">Qualification Overview</h2>
            <button
              onClick={handleRequalify}
              disabled={isRequalifying}
              className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 hover:border-purple-500 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed group"
              title="Re-qualify this call with updated criteria"
            >
              {isRequalifying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-300"></div>
                  <span>Re-qualifying...</span>
                </>
              ) : (
                <>
                  <RefreshCw
                    size={16}
                    className="group-hover:text-purple-400 transition-colors"
                  />
                  <span className="group-hover:text-purple-400 transition-colors">
                    Re-qualify
                  </span>
                </>
              )}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Lead Score */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-2">Lead Score</div>
              <div className="flex items-baseline gap-2">
                <div
                  className={`text-3xl font-bold ${getScoreColor(
                    qualification.qualificationScore,
                  )}`}
                >
                  {qualification.qualificationScore}
                </div>
                <div className="text-sm text-gray-400">/100</div>
              </div>
              <div
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${getLevelColor(
                  qualification.qualificationLevel,
                )}`}
              >
                {qualification.qualificationLevel.toUpperCase()}
              </div>
            </div>

            {/* Script Score */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-2">Script Adherence</div>
              <div className="flex items-baseline gap-2">
                <div
                  className={`text-3xl font-bold ${getScoreColor(
                    qualification.scriptAdherenceScore || 0,
                  )}`}
                >
                  {qualification.scriptAdherenceScore || 0}
                </div>
                <div className="text-sm text-gray-400">/100</div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {qualification.scriptSectionsCompleted?.length || 0}/6 sections
              </div>
            </div>

            {/* Objection Score */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="text-xs text-gray-400 mb-2">
                Objection Handling
              </div>
              <div className="flex items-baseline gap-2">
                <div
                  className={`text-3xl font-bold ${getScoreColor(
                    qualification.objectionHandlingScore || 0,
                  )}`}
                >
                  {qualification.objectionHandlingScore || 0}
                </div>
                <div className="text-sm text-gray-400">/100</div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                {qualification.objectionsEncountered?.length || 0} objections
              </div>
            </div>
          </div>

          {/* Status Badges */}
          <div className="flex flex-wrap gap-2">
            <div
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                qualification.isQualified
                  ? "bg-green-400/10 text-green-400"
                  : "bg-red-400/10 text-red-400"
              }`}
            >
              {qualification.isQualified ? "✓ Qualified" : "✗ Not Qualified"}
            </div>
            {qualification.appointmentBooked && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-400/10 text-blue-400">
                📅 Appointment Booked
              </div>
            )}
            {qualification.meetsSpendThreshold && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-400/10 text-purple-400">
                💰 Meets Budget
              </div>
            )}
            {qualification.customerHappyToProceed && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-cyan-400/10 text-cyan-400">
                ✓ Verbal Agreement
              </div>
            )}
          </div>
        </div>

        {/* Business Criteria */}
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">
            Business Qualification Criteria
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Current Spend */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  Monthly Spend
                </div>
                {qualification.meetsSpendThreshold ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              {qualification.currentSpendMentioned ? (
                <>
                  <div className="text-2xl font-bold text-white mb-1">
                    {qualification.currentSpend !== null
                      ? `£${qualification.currentSpend}`
                      : "Not specified"}
                  </div>
                  <div className="text-xs text-gray-500">
                    Threshold: £75+{" "}
                    {qualification.meetsSpendThreshold ? "(Met)" : "(Not Met)"}
                  </div>
                  {qualification.customerHappyToProceed && (
                    <div className="text-xs text-cyan-400 mt-1">
                      ✓ Customer agreed to £75+ pricing
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-gray-500">Not mentioned</div>
              )}
            </div>

            {/* System Age */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  System Age
                </div>
                {qualification.meetsSystemAgeThreshold ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              {qualification.phoneSystemAgeMentioned ? (
                <>
                  <div className="text-2xl font-bold text-white mb-1">
                    {qualification.phoneSystemAge} years
                  </div>
                  <div className="text-xs text-gray-500">
                    Threshold: ≥3 years{" "}
                    {qualification.meetsSystemAgeThreshold
                      ? "(Met)"
                      : "(Not Met)"}
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">Not mentioned</div>
              )}
            </div>

            {/* Handsets */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  Handsets
                </div>
                {qualification.meetsHandsetThreshold ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              {qualification.handsetsMentioned ? (
                <>
                  <div className="text-2xl font-bold text-white mb-1">
                    {qualification.numberOfHandsets}
                  </div>
                  <div className="text-xs text-gray-500">
                    Threshold: &gt;1{" "}
                    {qualification.meetsHandsetThreshold
                      ? "(Met)"
                      : "(Not Met)"}
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">Not mentioned</div>
              )}
            </div>

            {/* Contract */}
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  Contract Remaining
                </div>
                {qualification.meetsContractThreshold ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              {qualification.contractMentioned ? (
                <>
                  <div className="text-2xl font-bold text-white mb-1">
                    {qualification.contractYearsRemaining} years
                  </div>
                  <div className="text-xs text-gray-500">
                    Threshold: ≤4 years{" "}
                    {qualification.meetsContractThreshold
                      ? "(Met)"
                      : "(Not Met)"}
                  </div>
                </>
              ) : (
                <div className="text-sm text-gray-500">Not mentioned</div>
              )}
            </div>
          </div>
        </div>

        {/* Services Pitched */}
        {qualification.servicesPitched &&
          qualification.servicesPitched.length > 0 && (
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Services Discussed</h3>
              <div className="flex flex-wrap gap-2">
                {qualification.servicesPitched.map((service, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 bg-purple-500/10 text-purple-300 rounded-lg text-sm"
                  >
                    {service}
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-3">
                Score: +{qualification.servicesPitchScore} points (
                {qualification.servicesPitched.length >= 2
                  ? "✓ Qualified"
                  : "✗ Need 2+"}
                )
              </div>
            </div>
          )}

        {/* Contact Information */}
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
          <h3 className="text-lg font-semibold mb-4">
            Contact Information Collected
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  Full Name
                </div>
                {qualification.hasFullName ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              <div className="text-sm text-white">
                {qualification.fullName || "Not collected"}
              </div>
            </div>

            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  Job Title
                </div>
                {qualification.hasJobTitle ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              <div className="text-sm text-white">
                {qualification.jobTitle || "Not collected"}
              </div>
            </div>

            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">
                  Email{" "}
                  <span className="text-xs text-gray-500">(Optional)</span>
                </div>
                {qualification.hasEmail ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-gray-500">—</div>
                )}
              </div>
              <div className="text-sm text-white">
                {qualification.email || "Not collected"}
              </div>
            </div>

            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="text-sm font-medium text-gray-300">Address</div>
                {qualification.hasFullAddress ? (
                  <div className="text-green-400">✓</div>
                ) : (
                  <div className="text-red-400">✗</div>
                )}
              </div>
              <div className="text-sm text-white">
                {qualification.fullAddress || "Not collected"}
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-500 mt-3">
            Contact Info Score: +{qualification.contactInfoScore} points
            {qualification.contactInfoScore === 20
              ? " (✓ All required fields)"
              : " (✗ Missing required fields)"}
          </div>
        </div>

        {/* Script Adherence */}
        {qualification.scriptAdherence && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">
              Script Adherence Analysis
            </h3>

            <div className="space-y-4">
              {/* Intro */}
              {qualification.scriptAdherence.intro && (
                <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-white">Introduction</div>
                    <div
                      className={`text-sm ${getQualityColor(
                        qualification.scriptAdherence.intro.quality,
                      )}`}
                    >
                      {qualification.scriptAdherence.intro.quality?.toUpperCase()}
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {qualification.scriptAdherence.intro.notes}
                  </p>
                  {qualification.scriptAdherence.intro.completed && (
                    <div className="text-xs text-green-400">
                      ✓ Section completed
                    </div>
                  )}
                </div>
              )}

              {/* Agenda */}
              {qualification.scriptAdherence.agenda && (
                <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-white">Agenda Setting</div>
                    <div
                      className={`text-sm ${getQualityColor(
                        qualification.scriptAdherence.agenda.quality,
                      )}`}
                    >
                      {qualification.scriptAdherence.agenda.quality?.toUpperCase()}
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {qualification.scriptAdherence.agenda.notes}
                  </p>
                  {qualification.scriptAdherence.agenda.completed && (
                    <div className="text-xs text-green-400">
                      ✓ Section completed
                    </div>
                  )}
                </div>
              )}

              {/* Qualification */}
              {qualification.scriptAdherence.qualification && (
                <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-white">
                      Qualification Questions
                    </div>
                    <div
                      className={`text-sm ${getQualityColor(
                        qualification.scriptAdherence.qualification.quality,
                      )}`}
                    >
                      {qualification.scriptAdherence.qualification.quality?.toUpperCase()}
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {qualification.scriptAdherence.qualification.notes}
                  </p>
                  {qualification.scriptAdherence.qualification
                    .questionsAsked && (
                    <div className="text-xs text-gray-500 mt-2">
                      Questions asked:{" "}
                      {qualification.scriptAdherence.qualification.questionsAsked.join(
                        ", ",
                      )}
                    </div>
                  )}
                  {qualification.scriptAdherence.qualification.completed && (
                    <div className="text-xs text-green-400 mt-2">
                      ✓ All questions asked
                    </div>
                  )}
                </div>
              )}

              {/* System Specific Questions */}
              {qualification.scriptAdherence.systemSpecificQuestions && (
                <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-white">
                      System-Specific Questions
                    </div>
                    <div
                      className={`text-sm ${getQualityColor(
                        qualification.scriptAdherence.systemSpecificQuestions
                          .quality,
                      )}`}
                    >
                      {qualification.scriptAdherence.systemSpecificQuestions.quality?.toUpperCase()}
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {
                      qualification.scriptAdherence.systemSpecificQuestions
                        .notes
                    }
                  </p>
                  {qualification.scriptAdherence.systemSpecificQuestions
                    .completed && (
                    <div className="text-xs text-green-400">
                      ✓ Section completed
                    </div>
                  )}
                </div>
              )}

              {/* Features & Benefits */}
              {qualification.scriptAdherence.featuresAndBenefits && (
                <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-white">
                      Features & Benefits
                    </div>
                    <div
                      className={`text-sm ${getQualityColor(
                        qualification.scriptAdherence.featuresAndBenefits
                          .quality,
                      )}`}
                    >
                      {qualification.scriptAdherence.featuresAndBenefits.quality?.toUpperCase()}
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {qualification.scriptAdherence.featuresAndBenefits.notes}
                  </p>
                  {qualification.scriptAdherence.featuresAndBenefits
                    .completed && (
                    <div className="text-xs text-green-400 mt-2">
                      ✓ Section completed
                    </div>
                  )}
                </div>
              )}

              {/* Close */}
              {qualification.scriptAdherence.close && (
                <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="font-medium text-white">
                      Close & Appointment
                    </div>
                    <div
                      className={`text-sm ${getQualityColor(
                        qualification.scriptAdherence.close.quality,
                      )}`}
                    >
                      {qualification.scriptAdherence.close.quality?.toUpperCase()}
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 mb-2">
                    {qualification.scriptAdherence.close.notes}
                  </p>
                  {qualification.scriptAdherence.close.completed && (
                    <div className="text-xs text-green-400 mt-2">
                      ✓ Section completed
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Objection Handling */}
        {qualification.objectionsEncountered &&
          qualification.objectionsEncountered.length > 0 && (
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">
                Objections & Handling
              </h3>

              <div className="space-y-4">
                {qualification.objectionsEncountered.map((objection, index) => {
                  const handling = qualification.objectionsHandled?.find(
                    (h) => h.objection === objection.objection,
                  );

                  const getHandlingColor = (quality) => {
                    switch (quality) {
                      case "excellent":
                        return "text-green-400 bg-green-400/10";
                      case "good":
                        return "text-blue-400 bg-blue-400/10";
                      case "poor":
                        return "text-orange-400 bg-orange-400/10";
                      case "not_addressed":
                        return "text-red-400 bg-red-400/10";
                      default:
                        return "text-gray-400 bg-gray-400/10";
                    }
                  };

                  return (
                    <div
                      key={index}
                      className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="font-medium text-white mb-1">
                            {objection.objection}
                          </div>
                          {objection.timestamp && (
                            <div className="text-xs text-gray-500">
                              {objection.timestamp}
                            </div>
                          )}
                        </div>
                        {handling && (
                          <div
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getHandlingColor(
                              handling.quality,
                            )}`}
                          >
                            {handling.quality?.replace("_", " ").toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="bg-[#0A0C0F] rounded-lg p-3 mb-3">
                        <div className="text-xs text-gray-500 mb-1">
                          Customer said:
                        </div>
                        <div className="text-sm text-gray-300 italic">
                          "{objection.customerStatement}"
                        </div>
                      </div>

                      {handling && (
                        <>
                          <div className="bg-[#0A0C0F] rounded-lg p-3 mb-2">
                            <div className="text-xs text-gray-500 mb-1">
                              Agent response:
                            </div>
                            <div className="text-sm text-gray-300">
                              {handling.agentResponse}
                            </div>
                          </div>

                          {handling.notes && (
                            <div className="text-xs text-gray-400 mt-2">
                              {handling.notes}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        {/* Appointment Details */}
        {qualification.appointmentBooked && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">
              Appointment Assessment
            </h3>

            <div className="bg-[#0C0E12] border border-[#22262F] rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="font-medium text-white">Appointment Booked</div>
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    qualification.appointmentGenuine
                      ? "bg-green-400/10 text-green-400"
                      : "bg-orange-400/10 text-orange-400"
                  }`}
                >
                  {qualification.appointmentGenuine ? "Genuine" : "Concerns"}
                </div>
              </div>

              {qualification.appointmentGenuineScore !== null && (
                <div className="mb-3">
                  <div className="text-xs text-gray-500 mb-1">
                    Genuineness Score
                  </div>
                  <div
                    className={`text-2xl font-bold ${getScoreColor(
                      qualification.appointmentGenuineScore,
                    )}`}
                  >
                    {qualification.appointmentGenuineScore}/100
                  </div>
                </div>
              )}

              {qualification.appointmentConcerns && (
                <div className="bg-[#0A0C0F] rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">Notes:</div>
                  <div className="text-sm text-gray-300">
                    {qualification.appointmentConcerns}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Reasoning */}
        {qualification.qualificationReasoning && (
          <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
            <h3 className="text-lg font-semibold mb-4">AI Analysis Summary</h3>
            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">
              {qualification.qualificationReasoning}
            </p>
          </div>
        )}

        {/* Flags */}
        {qualification.qualificationFlags &&
          qualification.qualificationFlags.length > 0 && (
            <div className="bg-[#13161B] border border-red-500/30 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4 text-red-400">
                Qualification Flags
              </h3>
              <div className="space-y-2">
                {qualification.qualificationFlags.map((flag, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 text-sm text-gray-300"
                  >
                    <span className="text-red-400 mt-1">⚠</span>
                    <span>{flag}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>
    );
  };

  const progressPercentage =
    audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  // Password gate — shown before loading call details
  if (isPasswordProtected && !passwordVerified) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <Lock className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Protected Recording</h2>
            <p className="text-gray-400 text-sm">
              This recording is password protected. Please enter the password to
              view.
            </p>
          </div>

          <div className="mb-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handlePasswordSubmit()}
              placeholder="Enter password"
              className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
              autoFocus
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => navigate("/transcripts")}
              className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handlePasswordSubmit}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              Unlock
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error || !call) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2">Error Loading Call</h2>
          <p className="text-gray-400 mb-4">{error || "Call not found"}</p>
          <button
            onClick={() => navigate("/transcripts")}
            className="px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Transcripts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="p-4 md:p-8 mx-auto">
        {/* Hidden audio element */}
        {call.recordingUrl && (
          <audio
            ref={audioRef}
            src={call.recordingUrl}
            onLoadedMetadata={handleAudioLoadedMetadata}
            onTimeUpdate={handleAudioTimeUpdate}
            onEnded={handleAudioEnded}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Call Details
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              ID: {call.id} • {formatDate(call.callStartTime)}
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={handleExportTranscript}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
              disabled={!call.transcripts || call.transcripts.length === 0}
            >
              <ExportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">
                {call.transcripts && call.transcripts.length > 0
                  ? "Export Transcript"
                  : "No Transcript"}
              </span>
            </button>

            <button
              onClick={handleDownloadAudio}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              disabled={!call.recordingUrl}
              style={{
                background: !call.recordingUrl
                  ? "gray"
                  : "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource: !call.recordingUrl
                  ? "none"
                  : "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: !call.recordingUrl
                  ? "none"
                  : `0px 36px 32px -16px #7F56D936,
         0px 8px 16px 0px #7F56D91F,
         0px 2px 4px -8px #7F56D914,
         0px 8px 24px -16px #FFFFFF3D inset,
         0px -24px 32px 0px #FFFFFF38 inset,
         0px 0px 12px 0px #FFFFFF inset`,
                borderImageSlice: "1",
                transition: "all 0.3s ease",
                cursor: !call.recordingUrl ? "not-allowed" : "pointer",
                opacity: !call.recordingUrl ? 0.6 : 1,
              }}
            >
              <DocumentDownload size={16} className="shrink-0 text-white" />
              <span className="whitespace-nowrap">
                {call.recordingUrl ? "Download Audio" : "No Audio"}
              </span>
            </button>

            <button
              onClick={() => navigate("/transcripts")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <ArrowLeft size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Back</span>
            </button>

            {/* Password Control Button */}
            {/* <button
              onClick={() => {
                if (isPasswordProtected) {
                  handleRemovePassword();
                } else {
                  setShowSetPasswordModal(true);
                }
              }}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium transition-colors min-w-0 ${
                isPasswordProtected
                  ? "bg-yellow-600 hover:bg-yellow-700 text-white"
                  : "bg-[#0C0E12] border border-gray-700 text-gray-300 hover:bg-gray-700"
              }`}
            >
              {isPasswordProtected ? (
                <>
                  <Lock className="h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">Protected</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 shrink-0" />
                  <span className="whitespace-nowrap">Set Password</span>
                </>
              )}
            </button> */}
          </div>
        </div>

        {/* {(reprocessSuccess || reprocessError) && (
          <div className="mb-6">
            {reprocessSuccess && (
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-green-400 font-medium">Success</span>
                  </div>
                  <p className="text-green-300 text-sm whitespace-pre-line ml-7">
                    {reprocessSuccess}
                  </p>
                </div>
                <button
                  onClick={() => setReprocessSuccess(null)}
                  className="text-green-400 hover:text-green-300 ml-4"
                >
                  <X size={20} />
                </button>
              </div>
            )}

            {reprocessError && (
              <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✗</span>
                    </div>
                    <span className="text-red-400 font-medium">Error</span>
                  </div>
                  <p className="text-red-300 text-sm ml-7">{reprocessError}</p>
                </div>
                <button
                  onClick={() => setReprocessError(null)}
                  className="text-red-400 hover:text-red-300 ml-4"
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>
        )}

      
        <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Call Processing</h2>
              <p className="text-sm text-gray-400 mt-1">
                Re-run AI analysis on this call recording
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                  call.status,
                )}`}
              >
                <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
                {call.status?.charAt(0).toUpperCase() + call.status?.slice(1)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={handleReTranscribe}
              className="flex flex-col items-start p-4 bg-[#0C0E12] border border-gray-700 rounded-lg hover:border-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              title={
                !call.recordingUrl
                  ? "No audio recording available"
                  : ["processing", "transcribing", "analyzing"].includes(
                        call.status,
                      )
                    ? "Call is currently being processed"
                    : "Re-transcribe this call"
              }
            >
              <div className="flex items-center gap-2 mb-2">
                {reprocessing.transcribing ? (
                  <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5 text-gray-400 group-hover:text-purple-400 transition-colors" />
                )}
                <span className="text-sm font-medium text-white group-hover:text-purple-400 transition-colors">
                  Re-transcribe
                </span>
              </div>
              <p className="text-xs text-gray-400 text-left">
                {reprocessing.transcribing
                  ? "Transcribing audio..."
                  : "Regenerate transcript from audio"}
              </p>
            </button>

            <button
              onClick={handleReAnalyze}
              className="flex flex-col items-start p-4 bg-[#0C0E12] border border-gray-700 rounded-lg hover:border-purple-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              title={
                !call.transcripts || call.transcripts.length === 0
                  ? "Call must have transcripts first"
                  : ["processing", "transcribing", "analyzing"].includes(
                        call.status,
                      )
                    ? "Call is currently being processed"
                    : "Re-analyze this call"
              }
            >
              <div className="flex items-center gap-2 mb-2">
                {reprocessing.analyzing ? (
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                )}
                <span className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors">
                  Re-analyze
                </span>
              </div>
              <p className="text-xs text-gray-400 text-left">
                {reprocessing.analyzing
                  ? "Analyzing call..."
                  : "Regenerate sentiment & insights"}
              </p>
            </button>

            
            <button
              onClick={handleReProcess}
              className="flex flex-col items-start p-4 bg-[#0C0E12] border border-gray-700 rounded-lg hover:border-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              title={
                !call.recordingUrl
                  ? "No audio recording available"
                  : ["processing", "transcribing", "analyzing"].includes(
                        call.status,
                      )
                    ? "Call is currently being processed"
                    : "Complete re-process"
              }
            >
              <div className="flex items-center gap-2 mb-2">
                {reprocessing.processing ? (
                  <Loader2 className="w-5 h-5 text-green-400 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5 text-gray-400 group-hover:text-green-400 transition-colors" />
                )}
                <span className="text-sm font-medium text-white group-hover:text-green-400 transition-colors">
                  Complete Re-process
                </span>
              </div>
              <p className="text-xs text-gray-400 text-left">
                {reprocessing.processing
                  ? "Processing call..."
                  : "Re-transcribe, analyze & qualify"}
              </p>
            </button>
          </div>

          <div className="mt-4 p-3 bg-[#0A0C0F] rounded-lg border border-gray-700/50">
            <div className="flex items-start gap-2">
              <div className="text-gray-400 mt-0.5">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-gray-400">
                  <span className="font-medium">Re-transcribe:</span> Deletes
                  and regenerates transcripts from audio
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  <span className="font-medium">Re-analyze:</span> Regenerates
                  sentiment and insights (requires transcripts)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  <span className="font-medium">Complete Re-process:</span> Full
                  pipeline - transcribe, analyze, and qualify
                </p>
              </div>
            </div>
          </div>
          {call.metadata?.lastReProcessingAt && (
            <div className="mt-4 text-xs text-gray-500">
              Last re-processed:{" "}
              {new Date(call.metadata.lastReProcessingAt).toLocaleString()}
              {call.metadata?.reProcessingAttempt > 1 &&
                ` (${call.metadata.reProcessingAttempt} times)`}
            </div>
          )}
        </div> */}
        {/* Main Content */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Column - Media Player & Info */}
          <div className="lg:w-1/2 space-y-6">
            {/* Media Player */}
            <div className="">
              {/* Speaker Icons */}
              <div className="grid grid-cols-2 mb-8 mx-auto">
                <div className="flex flex-col items-center p-10 border-[#22262F] border rounded-tl-lg bg-[#13161B]">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#0C0E12] rounded-full flex items-center justify-center mb-2">
                    <Profile
                      size={20}
                      className="sm:w-5 sm:h-5 md:w-6 md:h-6"
                    />
                  </div>
                  <span className="text-xs text-gray-400 mt-2">Caller</span>
                  <span className="text-sm text-white mt-1">
                    {call.callerId || "Unknown"}
                  </span>
                </div>
                <div className="flex flex-col items-center p-10 border-[#22262F] border rounded-tr-lg bg-[#13161B]">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#0C0E12] rounded-full flex items-center justify-center mb-2">
                    <Profile
                      size={20}
                      className="sm:w-5 sm:h-5 md:w-6 md:h-6"
                    />
                  </div>
                  <span className="text-xs text-gray-400 mt-2">Agent</span>
                  <span className="text-sm text-white mt-1">
                    {call.metadata?.webexOwnerEmail ||
                      call.user?.name ||
                      "Agent"}
                  </span>
                </div>
                <div className="flex flex-col items-center p-10 border-[#22262F] border rounded-bl-lg bg-[#13161B]">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#0C0E12] rounded-full flex items-center justify-center mb-2">
                    <Clock
                      size={20}
                      className="sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-400"
                    />
                  </div>
                  <span className="text-xs text-gray-400 mt-2">Duration</span>
                  <span className="text-sm text-white mt-1">
                    {formatDuration(call.duration)}
                  </span>
                </div>
                <div className="flex flex-col items-center p-10 border-[#22262F] border rounded-br-lg bg-[#13161B]">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#0C0E12] rounded-full flex items-center justify-center mb-2">
                    <Flag
                      size={20}
                      className="sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-400"
                    />
                  </div>
                  <span className="text-xs text-gray-400 mt-2">Status</span>
                  <span
                    className={`text-sm mt-1 ${
                      getStatusColor(call.status).split(" ")[0]
                    }`}
                  >
                    {call.status?.charAt(0).toUpperCase() +
                      call.status?.slice(1)}
                  </span>
                </div>
              </div>

              {/* Media Controls */}
              {call.recordingUrl && (
                <div className="flex items-center gap-4 w-full">
                  <button
                    onClick={() => {
                      if (audioRef.current) {
                        if (isPlaying) {
                          audioRef.current.pause();
                        } else {
                          audioRef.current.play();
                        }
                        setIsPlaying(!isPlaying);
                      }
                    }}
                    className="w-12 h-12 rounded-full flex items-center justify-center transition-colors flex-shrink-0 hover:bg-gray-700"
                  >
                    {isPlaying ? (
                      <Pause size={20} className="text-[#61656C]" />
                    ) : (
                      <Play size={20} className="text-[#61656C]" />
                    )}
                  </button>

                  <div className="flex-1 md:flex items-center gap-4 max-w-2xl mx-4 hidden">
                    <span className="text-sm text-gray-400 min-w-[40px] text-right">
                      {formatTime(currentTime)}
                    </span>
                    <div
                      className="flex-1 relative cursor-pointer"
                      onClick={handleSeek}
                    >
                      <div className="h-2 bg-gray-700 rounded-full">
                        <div
                          className="h-2 bg-[#7F56D9] rounded-full relative"
                          style={{ width: `${progressPercentage}%` }}
                        >
                          <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <span className="text-sm text-gray-400 min-w-[40px]">
                      {formatTime(Math.floor(audioDuration))}
                    </span>
                  </div>

                  <div className="flex items-center flex-shrink-0">
                    <button
                      onClick={() => {
                        if (audioRef.current) {
                          audioRef.current.muted = !isMuted;
                          setIsMuted(!isMuted);
                        }
                      }}
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      {isMuted ? (
                        <VolumeCross size={15} className="text-[#61656C]" />
                      ) : (
                        <VolumeHigh size={15} className="text-[#61656C]" />
                      )}
                    </button>
                    <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                      <Forward size={15} className="text-[#61656C]" />
                    </button>
                    <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                      <Maximize2 size={15} className="text-[#61656C]" />
                    </button>
                  </div>
                </div>
              )}

              {!call.recordingUrl && (
                <div className="text-center py-4 text-gray-400">
                  No audio recording available for this call
                </div>
              )}
            </div>

            {/* Summary, Tags & Notes Box */}
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6 space-y-6">
              {/* Summary */}
              {call.insights?.summary && (
                <div>
                  <h2 className="text-lg font-semibold mb-4">Summary</h2>
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {call.insights.summary}
                  </p>
                </div>
              )}

              {/* Tags */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Tags</h2>
                  <Tag size={16} className="text-gray-400" />
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {tags.map((tag, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1 px-3 py-1 bg-purple-500/10 text-purple-300 rounded-full text-xs"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="text-purple-300 hover:text-white"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                    className="flex-1 bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-1 text-sm text-white focus:outline-none focus:border-purple-500"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Notes</h2>
                  {editingNotes ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveNotes}
                        className="p-1 text-green-400 hover:text-green-300"
                      >
                        <Save size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setEditingNotes(false);
                          setNotes(call.metadata?.notes || "");
                        }}
                        className="p-1 text-red-400 hover:text-red-300"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="p-1 text-gray-400 hover:text-white"
                    >
                      <Edit3 size={16} />
                    </button>
                  )}
                </div>

                {editingNotes ? (
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full bg-[#0C0E12] border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
                    rows="4"
                    placeholder="Add notes about this call..."
                  />
                ) : (
                  <p className="text-sm text-gray-300 min-h-[80px]">
                    {notes || "No notes added yet."}
                  </p>
                )}
              </div>
            </div>

            {/* Action Items & Flagged Status */}
            <div className="space-y-6">
              {call.insights?.actionItems &&
                call.insights.actionItems.length > 0 && (
                  <div className="bg-[#13161B] border border-[#22262F] rounded-xl p-6">
                    <h2 className="text-lg font-semibold mb-4">Action Items</h2>
                    <ul className="space-y-3">
                      {call.insights.actionItems.map((actionItem, index) => (
                        <li
                          key={index}
                          className="text-sm text-gray-300 flex items-start gap-3"
                        >
                          <span className="mt-1 w-1.5 h-1.5 bg-purple-500 rounded-full flex-shrink-0"></span>
                          <div className="flex-1">
                            <span className="block">{actionItem.item}</span>
                            {actionItem.speaker && (
                              <span className="text-xs text-gray-400 mt-1 block">
                                Assigned to: {actionItem.speaker}
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

              {call.insights?.flaggedForReview && (
                <div className="bg-[#13161B] border border-red-500/30 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Flag size={16} className="text-red-400" />
                    <h2 className="text-lg font-semibold text-red-400">
                      Flagged for Review
                    </h2>
                  </div>
                  <p className="text-sm text-gray-300">
                    This call has been flagged for further review due to
                    detected issues.
                  </p>
                  {call.insights.escalationRequired && (
                    <p className="text-sm text-red-300 mt-2">
                      Escalation to management is recommended.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Qualification Details */}
            <QualificationDetails qualification={call.qualification} />
          </div>

          {/* Right Column - Transcript */}
          <div className="lg:w-1/2">
            <div className="bg-[#13161B] border border-[#22262F] rounded-xl h-[800px] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-[#22262F] flex-shrink-0">
                <h2 className="text-lg font-semibold">Transcript</h2>
                <div className="flex items-center gap-2">
                  {call.insights?.averageSentiment && (
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getSentimentColor(
                        call.insights.averageSentiment,
                      )} bg-opacity-10`}
                    >
                      {call.insights.averageSentiment?.charAt(0).toUpperCase() +
                        call.insights.averageSentiment?.slice(1)}{" "}
                      Sentiment
                    </span>
                  )}
                  <button className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
                    <MoreVertical size={16} className="text-gray-400" />
                  </button>
                </div>
              </div>

              <div
                ref={transcriptContainerRef}
                className="p-6 space-y-6 flex-1 overflow-y-auto scroll-smooth"
                style={{ scrollBehavior: "smooth" }}
              >
                {call.transcripts && call.transcripts.length > 0 ? (
                  call.transcripts.map((transcript, index) => {
                    const isActive = isTranscriptActive(
                      transcript.startTime,
                      index,
                    );

                    return (
                      <div
                        key={index}
                        ref={(el) => (transcriptRefs.current[index] = el)}
                        className={`flex gap-4 group transition-all duration-300 ${
                          isActive
                            ? "bg-purple-500/10 -mx-2 px-2 py-2 rounded-lg"
                            : ""
                        }`}
                      >
                        <div className="flex flex-col items-center min-w-[60px]">
                          <button
                            onClick={() =>
                              handleTranscriptTimeClick(transcript.startTime)
                            }
                            className={`text-xs transition-colors cursor-pointer ${
                              isActive
                                ? "text-purple-400 font-semibold"
                                : "text-gray-400 hover:text-purple-400 group-hover:text-purple-400"
                            }`}
                            title={`Jump to ${formatTranscriptTime(
                              transcript.startTime,
                            )}`}
                          >
                            {formatTranscriptTime(transcript.startTime)}
                          </button>
                        </div>
                        <div className="flex-1">
                          <div
                            className={`text-sm font-medium mb-1 ${
                              isActive ? "text-purple-300" : "text-white"
                            }`}
                          >
                            {transcript.speakerLabel || "Speaker"}:
                          </div>
                          <p
                            className={`text-sm leading-relaxed ${
                              isActive ? "text-white" : "text-[#94979C]"
                            }`}
                          >
                            {transcript.text}
                          </p>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    No transcript available
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AskGenziButton />

      {/* Set Password Modal */}
      {showSetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#1a1d23] border border-gray-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">
              Set Recording Password
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Protect this recording with a password. Users will need to enter
              the password to view it.
            </p>

            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">
                Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSetPassword()}
                placeholder="Enter password (min 6 characters)"
                className="w-full bg-[#0C0E12] border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSetPasswordModal(false);
                  setNewPassword("");
                }}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSetPassword}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Set Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallDetail;
